import { ObjectExpression, ObjectProperty } from "@babel/types";
import { singleton } from "tsyringe";
import { ObjectResult, Result } from "../../../../../types/codeEditor";
import {
  IEvaluatedSchemaMetadata,
  SchemaFormat,
  SchemaType
} from "../../../types";
import { BaseJavascriptFormatSchemaVisitor } from "./base";

@singleton()
export class JavascriptIndexSchemaVisitor extends BaseJavascriptFormatSchemaVisitor {
  get schemaFormat(): SchemaFormat {
    return "javascript";
  }

  get schemaTypes(): SchemaType[] {
    return ["index"];
  }

  get usingSourceGroup(): boolean {
    return true;
  }

  private validateIndexObject(moduleObject: ObjectExpression): Result {
    const indexProperty = moduleObject.properties
      .filter((property) => property.type === "ObjectProperty")
      .map((property) => property as ObjectProperty)
      .find(
        (property) =>
          property.key.type === "Identifier" && property.key.name === "index"
      );

    if (!indexProperty) {
      return Result.fail([{ description: "Index object must be defined" }]);
    }

    // Validate fields inside the index object
    if (indexProperty.value.type === "ObjectExpression") {
      const indexObjectExpression = indexProperty.value;

      // Find the fields property inside the index object
      const fieldsProperty = indexObjectExpression.properties
        .filter((property) => property.type === "ObjectProperty")
        .map((property) => property as ObjectProperty)
        .find(
          (property) =>
            property.key.type === "Identifier" && property.key.name === "fields"
        );

      if (fieldsProperty && fieldsProperty.value.type === "ObjectExpression") {
        const fieldsObject = fieldsProperty.value;

        // Loop through all fields in the fields object
        for (const fieldProperty of fieldsObject.properties) {
          if (fieldProperty.type !== "ObjectProperty") {
            continue;
          }

          const fieldDescriptor = fieldProperty;

          // Check if the field descriptor has a metadata object
          if (fieldDescriptor.value.type === "ObjectExpression") {
            const fieldDescriptorObject = fieldDescriptor.value;

            // Find the metadata property in the field descriptor
            const metadataProperty = fieldDescriptorObject.properties
              .filter((property) => property.type === "ObjectProperty")
              .map((property) => property as ObjectProperty)
              .find(
                (property) =>
                  property.key.type === "Identifier" &&
                  property.key.name === "metadata"
              );

            if (
              metadataProperty &&
              metadataProperty.value.type === "ObjectExpression"
            ) {
              const metadataObject = metadataProperty.value;

              // Find the description property in the metadata
              const descriptionProperty = metadataObject.properties
                .filter((property) => property.type === "ObjectProperty")
                .map((property) => property as ObjectProperty)
                .find(
                  (property) =>
                    property.key.type === "Identifier" &&
                    property.key.name === "description"
                );

              if (
                descriptionProperty &&
                descriptionProperty.value.type === "StringLiteral"
              ) {
                const description = descriptionProperty.value.value;
                if (description.length > 50) {
                  // Get the field name for better error messaging
                  let fieldName = "unknown";
                  if (fieldDescriptor.key.type === "Identifier") {
                    fieldName = fieldDescriptor.key.name;
                  } else if (fieldDescriptor.key.type === "StringLiteral") {
                    fieldName = fieldDescriptor.key.value;
                  }

                  return Result.fail([
                    {
                      description: `Metadata description for field '${fieldName}' must not be longer than 50 characters`
                    }
                  ]);
                }
              }
            }
          }
        }
      }
    }

    return Result.ok();
  }

  visitModuleObject(
    moduleObject: ObjectExpression
  ): ObjectResult<IEvaluatedSchemaMetadata> {
    const triggerSourceEntityTypesExtractionResult =
      this.validateTriggers(moduleObject);
    const indexMethodResult = this.validateIndexObject(moduleObject);
    if (!indexMethodResult.valid) {
      return ObjectResult.fail<IEvaluatedSchemaMetadata>(
        indexMethodResult.errors
      );
    }

    const propertiesValidation = this.validateProperties(moduleObject);
    if (!propertiesValidation.valid) {
      return ObjectResult.fail<IEvaluatedSchemaMetadata>(
        propertiesValidation.errors
      );
    }

    if (
      !triggerSourceEntityTypesExtractionResult.valid ||
      !indexMethodResult.valid
    ) {
      return ObjectResult.fail<IEvaluatedSchemaMetadata>([
        ...(triggerSourceEntityTypesExtractionResult.errors ?? []),
        ...(indexMethodResult.errors ?? [])
      ]);
    }

    return ObjectResult.ok<IEvaluatedSchemaMetadata>({
      sourceEntityTypes: triggerSourceEntityTypesExtractionResult.value ?? []
    });
  }
}
